// Referrals board — /referrals (Aug 20, 2026)
//
// One screen for the whole customer-referral lifecycle:
//   • To call     — new/contacted referrals waiting on an RM. Call/text right
//                   from the card, then "Appointment set" (creates the lead,
//                   pre-filled, and links it) or "No thanks".
//   • In progress — referrals that became deals, tracked to the build.
//   • Payouts     — who is owed the $250. This is the backstop Greg asked for
//                   so a fee can't get lost if it's missed on the finance board.
//
// Scope: RLS lets any authenticated staff READ referrals, so the Mine/All
// toggle is a UI convenience, not the security boundary. WRITES are gated —
// the assigned RM, the referring deal's owner/split, and managers can work a
// referral; only finance-tier can touch money (trg_referrals_payout_guard).
//
// Oct 20, 2026 (Greg): a fee approves itself once the referred roof is built
// and that job's first payment is in (referral_try_auto_approve); finance can
// still approve one sooner, and Shannon pays. A referral that matched a deal
// already in the CRM is linked to that job by a manager ("Link to this job")
// so its fee follows that build.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useTabParam } from "../lib/useTabParam";
import { useRealtimeTable } from "../lib/useRealtimeTable";
import DealLink from "../components/DealLink";
import ManualEntryModal, { type RosterMember } from "../components/deal/ManualEntryModal";
import {
  type Referral,
  REFERRAL_FEE, STATUS_LABELS, STATUS_CLASSES, PAYOUT_LABELS, PAYOUT_CLASSES,
  fetchReferrals, markContacted, closeReferral, reopenReferral, linkReferralToDeal,
  approvePayout, markPayoutPaid, voidPayout, logStaffReferral,
  referredAddress, referrerAddress, money, daysSince, telHref, smsHref,
} from "../lib/referrals";

type TabKey = "queue" | "progress" | "payouts" | "all";
const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "queue",    label: "To call" },
  { key: "progress", label: "In progress" },
  { key: "payouts",  label: "Payouts" },
  { key: "all",      label: "All" },
];

const FINANCE_ROLES = new Set(["admin", "sales_director", "finance"]);
const MANAGER_ROLES = new Set([
  "admin", "sales_director", "finance", "asm", "team_lead",
  "roof_director", "director_of_operations", "roof_team_manager",
]);

type DealLite = { id: string; homeowner_name: string | null; stage: string | null;
                  build_completed_date: string | null; owner_id: string | null;
                  split_rm_id: string | null; post_build_balance_remaining: number | string | null };

export default function Referrals() {
  const { member, role, isSuperUser } = useAuth();
  const [tab, setTab] = useTabParam<TabKey>("tab", "queue", TABS.map((t) => t.key));
  const [params, setParams] = useSearchParams();
  const focusId = params.get("focus");

  const [rows, setRows] = useState<Referral[]>([]);
  const [roster, setRoster] = useState<RosterMember[]>([]);
  const [deals, setDeals] = useState<Record<string, DealLite>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const [logOpen, setLogOpen] = useState(false);

  const canSeeMoney = isSuperUser || FINANCE_ROLES.has(role ?? "");
  const isManager = isSuperUser || MANAGER_ROLES.has(role ?? "");

  useEffect(() => { setScope(isManager ? "all" : "mine"); }, [isManager]);

  const load = useCallback(async () => {
    try {
      setErr(null);
      const [refs, rosterRes] = await Promise.all([
        fetchReferrals(),
        supabase.from("team_roster").select("id, full_name, role").eq("is_active", true),
      ]);
      setRows(refs);
      setRoster((rosterRes.data ?? []) as RosterMember[]);

      // Resolve both sides of every referral in ONE query. Deals RLS may hide
      // some rows from an RM — the cards fall back to the referral's own
      // snapshot fields, so a miss degrades gracefully.
      const ids = [
        ...new Set(refs.flatMap((r) => [r.referrer_deal_id, r.referred_deal_id, r.duplicate_of_deal_id]
          .filter(Boolean) as string[])),
      ];
      if (ids.length) {
        const { data } = await supabase
          .from("deals")
          .select("id, homeowner_name, stage, build_completed_date, owner_id, split_rm_id, post_build_balance_remaining")
          .in("id", ids);
        setDeals(Object.fromEntries(((data ?? []) as DealLite[]).map((d) => [d.id, d])));
      } else {
        setDeals({});
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't load referrals.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("referrals", () => { void load(); });

  const rosterName = useCallback(
    (id: string | null) => (id ? roster.find((m) => m.id === id)?.full_name ?? null : null),
    [roster],
  );

  /** "Mine" = assigned to me, or I own/split the referring deal. Mirrors the
   *  referral_can_work() predicate the DB enforces on writes. */
  const isMine = useCallback((r: Referral) => {
    if (!member?.id) return false;
    if (r.assigned_to === member.id) return true;
    const d = r.referrer_deal_id ? deals[r.referrer_deal_id] : null;
    return !!d && (d.owner_id === member.id || d.split_rm_id === member.id);
  }, [member?.id, deals]);

  // Scope (Mine/All) but NOT search. The money banner totals from this so the
  // dollar figure always matches the toggle the user is looking at -- and
  // doesn't jitter while they type in the search box.
  const scoped = useMemo(() => {
    if (focusId) return rows;
    return scope === "mine" ? rows.filter(isMine) : rows;
  }, [rows, scope, isMine, focusId]);

  const filtered = useMemo(() => {
    if (focusId) return rows.filter((r) => r.id === focusId);
    const needle = q.trim().toLowerCase();
    if (!needle) return scoped;
    return scoped.filter((r) =>
      [r.referred_name, r.referrer_name, r.referred_phone, referredAddress(r)]
        .filter(Boolean).some((s) => (s as string).toLowerCase().includes(needle)));
  }, [rows, scoped, q, focusId]);

  // Oldest first: a referral sitting for four days is the one that needs the
  // call, not the one that came in this morning. (fetchReferrals returns
  // newest-first, which is right for the other tabs.)
  const queue    = filtered
    .filter((r) => r.status === "new" || r.status === "contacted")
    .slice()
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const progress = filtered.filter((r) => r.status === "appt_scheduled" && r.payout_status !== "paid");
  const payouts  = filtered.filter((r) => ["earned", "approved", "paid"].includes(r.payout_status));

  // Duplicate claims sit at 'earned' so Chad can see and void them, but they
  // are NOT owed — excluding them keeps the banner total honest.
  const owed = scoped.filter((r) => r.payout_status === "earned" && !r.superseded_by_referral_id);
  const duplicates = scoped.filter((r) => r.payout_status === "earned" && r.superseded_by_referral_id);
  const readyToPay = scoped.filter((r) => r.payout_status === "approved");
  const owedTotal = [...owed, ...readyToPay].reduce((s, r) => s + Number(r.payout_amount ?? 0), 0);

  const visible = focusId ? filtered
    : tab === "queue" ? queue
    : tab === "progress" ? progress
    : tab === "payouts" ? payouts
    : filtered;

  const counts: Record<TabKey, number> = {
    queue: queue.length, progress: progress.length,
    payouts: payouts.length, all: filtered.length,
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-5 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-bardin-ink">Referrals</h1>
          <p className="text-sm text-slate-500">
            {money(REFERRAL_FEE)} to the customer when their referral’s roof gets built.
          </p>
        </div>
        <button
          onClick={() => setLogOpen(true)}
          className="px-3 py-2 rounded-lg bg-bardin-orange text-white text-sm font-semibold hover:opacity-90"
        >
          + Log a referral
        </button>
      </div>

      {/* Money backstop — always visible, every tab. */}
      {(owed.length > 0 || readyToPay.length > 0) && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-bold text-amber-900">
                💰 {money(owedTotal)} in referral fees outstanding
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                {owed.length} not yet approved · {readyToPay.length} approved and ready for Shannon to pay
                {duplicates.length > 0 && ` · ${duplicates.length} duplicate claim${duplicates.length === 1 ? "" : "s"} to void`}
              </p>
            </div>
            {tab !== "payouts" && (
              <button onClick={() => setTab("payouts")}
                className="text-xs font-semibold text-amber-900 underline underline-offset-2">
                Open payouts →
              </button>
            )}
          </div>
        </div>
      )}

      {focusId && (
        <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-sm flex items-center justify-between">
          <span className="text-sky-800">Showing one referral from your alert.</span>
          <button onClick={() => {
              // Read window.location.search, NOT the closure-captured `params`:
              // useTabParam writes the URL via history.replaceState, so the
              // react-router snapshot can be stale and clobber a tab change.
              // Same bug class as the Apr 29 useTabParam race.
              const p = new URLSearchParams(window.location.search);
              p.delete("focus");
              setParams(p, { replace: true });
            }}
            className="text-sky-700 font-semibold text-xs underline">Show all</button>
        </div>
      )}

      {!focusId && (
        <>
          <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${
                  tab === t.key
                    ? "border-bardin-orange text-bardin-orange"
                    : "border-transparent text-slate-500 hover:text-slate-700"}`}>
                {t.label}
                <span className="ml-1.5 text-xs text-slate-400">{counts[t.key]}</span>
              </button>
            ))}
          </div>

          <div className="flex gap-2 items-center flex-wrap">
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, phone, address…"
              className="flex-1 min-w-[200px] border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <div className="flex rounded-lg border border-slate-300 overflow-hidden text-sm">
              {(["mine", "all"] as const).map((s) => (
                <button key={s} onClick={() => setScope(s)}
                  className={`px-3 py-2 ${scope === s ? "bg-bardin-orange text-white" : "bg-white text-slate-600"}`}>
                  {s === "mine" ? "Mine" : "All"}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {err && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{err}</div>}
      {loading && <p className="text-sm text-slate-400">Loading referrals…</p>}

      {!loading && visible.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center">
          <div className="text-4xl mb-2">🤝</div>
          <p className="text-sm font-semibold text-slate-600">
            {tab === "queue" ? "No referrals waiting on a call." :
             tab === "progress" ? "No referrals in progress." :
             tab === "payouts" ? "No referral fees outstanding." : "No referrals yet."}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Customers submit these from their portal, or tap “Log a referral” when one comes in by phone.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {visible.map((r) => (
          <ReferralCard
            key={r.id} r={r} allRows={rows} deals={deals} roster={roster} rosterName={rosterName}
            canSeeMoney={canSeeMoney} isManager={isManager} actorId={member?.id ?? null}
            defaultOwnerId={member?.id ?? null} onChanged={load}
          />
        ))}
      </div>

      {logOpen && (
        <LogReferralModal onClose={() => setLogOpen(false)}
          onLogged={() => { setLogOpen(false); void load(); }} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ReferralCard({
  r, deals, roster, rosterName, canSeeMoney, isManager, actorId, defaultOwnerId, onChanged, allRows,
}: {
  r: Referral;
  allRows: Referral[];
  deals: Record<string, DealLite>;
  roster: RosterMember[];
  rosterName: (id: string | null) => string | null;
  canSeeMoney: boolean;
  isManager: boolean;
  actorId: string | null;
  defaultOwnerId: string | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeNote, setCloseNote] = useState("");
  const [paying, setPaying] = useState(false);
  const [payMethod, setPayMethod] = useState("check");
  const [payRef, setPayRef] = useState("");
  const [apptOpen, setApptOpen] = useState(false);
  const [confirmOverBal, setConfirmOverBal] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  const referredDeal = r.referred_deal_id ? deals[r.referred_deal_id] : null;
  // First referrer wins; this one is a duplicate claim awaiting Chad's void.
  const supersededBy = r.superseded_by_referral_id
    ? allRows.find((x) => x.id === r.superseded_by_referral_id) ?? null
    : null;
  const isDuplicateClaim = !!r.superseded_by_referral_id;
  // build_completed_date means BUILT, not COLLECTED -- so a fee is often owed
  // while the job still has money out. Soft gate: show it, let Chad decide.
  const outstanding = Number(referredDeal?.post_build_balance_remaining ?? 0);
  const hasBalance = outstanding > 0;
  const dupDeal = r.duplicate_of_deal_id ? deals[r.duplicate_of_deal_id] : null;
  const age = daysSince(r.created_at);
  const tel = telHref(r.referred_phone);
  const sms = smsHref(r.referred_phone);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setLocalErr(null);
    try { await fn(); onChanged(); }
    catch (e) { setLocalErr(e instanceof Error ? e.message : "Something went wrong."); }
    finally { setBusy(false); }
  };

  const stale = r.status === "new" && (age ?? 0) >= 2;

  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${
      stale ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-200"}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-bardin-ink">{r.referred_name}</h3>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${STATUS_CLASSES[r.status]}`}>
              {STATUS_LABELS[r.status]}
            </span>
            {r.payout_status !== "pending" && (
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${PAYOUT_CLASSES[r.payout_status]}`}>
                {money(r.payout_amount ?? REFERRAL_FEE)} · {PAYOUT_LABELS[r.payout_status]}
              </span>
            )}
            {stale && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
              {age}d — no contact yet
            </span>}
            {isDuplicateClaim && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold"
                title="Another customer referred this homeowner first">
                ⚠ Duplicate claim — void
              </span>
            )}
            {r.claim_filed_at && r.payout_status === "pending" && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold"
                title="Claim called in — the fee pays once the roof is built">
                📝 Claim filed · fee pending
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {r.referred_phone && <span className="font-medium">{r.referred_phone}</span>}
            {r.referred_phone && referredAddress(r) && <span className="text-slate-300"> · </span>}
            {referredAddress(r)}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Referred by{" "}
            {r.referrer_deal_id
              ? <DealLink id={r.referrer_deal_id} className="font-semibold text-bardin-teal">{r.referrer_name}</DealLink>
              : <span className="font-semibold">{r.referrer_name}</span>}
            {age !== null && <> · {age === 0 ? "today" : `${age}d ago`}</>}
            {r.assigned_to && <> · owner {rosterName(r.assigned_to) ?? "—"}</>}
            {r.source === "staff" && <> · logged by {rosterName(r.created_by) ?? "staff"}</>}
          </p>
        </div>
      </div>

      {r.notes && <p className="mt-2 text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2">“{r.notes}”</p>}

      {/* Duplicate flag — we still pay if this customer sent them. Until a
          manager links it to that job, the fee can't follow the build (Matt
          Hodge's two built referrals sat "in flight" for six weeks this way). */}
      {r.duplicate_of_deal_id && (
        <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
          <span className="font-semibold">⚠ Already in the CRM</span>{" "}
          {dupDeal
            ? <>— matches <DealLink id={dupDeal.id} className="underline font-semibold">{dupDeal.homeowner_name ?? "an existing deal"}</DealLink>{dupDeal.stage ? ` (${dupDeal.stage.replace(/_/g, " ")})` : ""}{dupDeal.build_completed_date ? `, built ${dupDeal.build_completed_date}` : ""}.</>
            : "— matches an existing deal."}{" "}
          {r.referred_deal_id
            ? "Per policy we still pay the fee."
            : "Per policy we still pay the fee: if this customer sent them, link it to that job so the fee follows its build."}
          {isManager && dupDeal && !r.referred_deal_id
            && r.status !== "no_thanks" && r.status !== "unqualified" && (
            <div className="mt-2">
              <button disabled={busy} onClick={() => {
                const who = dupDeal.homeowner_name ?? "that customer";
                const ok = window.confirm(dupDeal.build_completed_date
                  ? `Link this referral to ${who}'s job? Their roof is already built, so ${r.referrer_name}'s ${money(r.payout_amount ?? REFERRAL_FEE)} is earned now and approves once that job's first payment is in.`
                  : `Link this referral to ${who}'s job? ${r.referrer_name}'s ${money(r.payout_amount ?? REFERRAL_FEE)} follows that job and pays once its roof is built.`);
                if (ok) void run(() => linkReferralToDeal(r.id, dupDeal.id));
              }} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold disabled:opacity-50">
                🔗 Link to {dupDeal.homeowner_name ?? "this"}'s job
              </button>
            </div>
          )}
        </div>
      )}

      {/* Progress once it converts */}
      {referredDeal && (
        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700">
          Became <DealLink id={referredDeal.id} className="font-semibold text-bardin-teal underline">
            {referredDeal.homeowner_name ?? "a deal"}
          </DealLink>
          {referredDeal.stage && <> · stage <span className="font-medium">{referredDeal.stage.replace(/_/g, " ")}</span></>}
          {referredDeal.build_completed_date
            ? <> · <span className="font-semibold text-emerald-700">built {referredDeal.build_completed_date}</span></>
            : <> · <span className="text-slate-500">fee pays once the build completes</span></>}
        </div>
      )}

      {localErr && <p className="mt-2 text-xs text-red-600">{localErr}</p>}

      {/* Actions — work queue */}
      {(r.status === "new" || r.status === "contacted") && !closing && (
        <div className="mt-3 flex flex-wrap gap-2">
          {tel && <a href={tel} className="px-3 py-1.5 rounded-lg bg-bardin-teal text-white text-xs font-semibold">📞 Call</a>}
          {sms && <a href={sms} className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-semibold">💬 Text</a>}
          {r.status === "new" && (
            <button disabled={busy} onClick={() => run(() => markContacted(r.id))}
              className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-700 disabled:opacity-50">
              Mark contacted
            </button>
          )}
          <button disabled={busy} onClick={() => setApptOpen(true)}
            className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50">
            ✓ Appointment set
          </button>
          <button disabled={busy} onClick={() => setClosing(true)}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-500 disabled:opacity-50">
            No thanks
          </button>
        </div>
      )}

      {closing && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
          <p className="text-xs font-semibold text-slate-700">Close this referral — what happened?</p>
          <input value={closeNote} onChange={(e) => setCloseNote(e.target.value)}
            placeholder="e.g. Not interested / roof is new / no damage"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button disabled={busy} onClick={() => run(async () => {
              await closeReferral(r.id, "no_thanks", closeNote); setClosing(false);
            })} className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-semibold disabled:opacity-50">
              No thanks
            </button>
            <button disabled={busy} onClick={() => run(async () => {
              await closeReferral(r.id, "unqualified", closeNote); setClosing(false);
            })} className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-600 disabled:opacity-50">
              Not qualified
            </button>
            <button onClick={() => setClosing(false)} className="px-3 py-1.5 text-xs text-slate-500">Cancel</button>
          </div>
        </div>
      )}

      {(r.status === "no_thanks" || r.status === "unqualified") && isManager && (
        <div className="mt-3 flex items-center gap-2">
          {r.resolution_note && <span className="text-xs text-slate-500 italic">“{r.resolution_note}”</span>}
          <button disabled={busy} onClick={() => run(() => reopenReferral(r.id))}
            className="text-xs font-semibold text-bardin-teal underline disabled:opacity-50">Reopen</button>
        </div>
      )}

      {/* Actions — money. Approval is automatic once the referred roof is built
          and that job's first payment is in; finance can approve sooner.
          Shannon pays. */}
      {canSeeMoney && r.payout_status === "earned" && isDuplicateClaim && (
        <div className="mt-3 rounded-lg bg-amber-50 border-2 border-amber-300 p-3">
          <p className="text-xs font-semibold text-amber-900">
            ⚠ Duplicate claim — {supersededBy?.referrer_name ?? "another customer"} referred{" "}
            {r.referred_name} first and wins the fee.
          </p>
          <p className="text-xs text-amber-800 mt-0.5">
            Only the first referrer is paid. Void this one so it stops showing as owed.
          </p>
          <button disabled={busy} onClick={() => run(async () => {
            await voidPayout(r.id,
              `Duplicate claim — ${supersededBy?.referrer_name ?? "another customer"} referred ${r.referred_name} first`);
          })} className="mt-2 px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-semibold disabled:opacity-50">
            Void this duplicate
          </button>
        </div>
      )}

      {canSeeMoney && r.payout_status === "earned" && !isDuplicateClaim && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3">
          <p className="text-xs font-semibold text-amber-900">
            {money(r.payout_amount ?? REFERRAL_FEE)} owed to {r.referrer_name}
            {referrerAddress(r) && <span className="font-normal"> · mail to {referrerAddress(r)}</span>}
          </p>
          <p className="mt-1 text-xs text-amber-800">
            Approves on its own once {referredDeal?.homeowner_name ?? "the referred job"}'s first payment is in.
            Approve it now to pay sooner.
          </p>
          {hasBalance && (
            <p className="mt-1.5 text-xs text-amber-900 bg-amber-100/70 border border-amber-300 rounded-lg px-2.5 py-1.5">
              ⚠ {money(outstanding)} still outstanding on {referredDeal?.homeowner_name ?? "the referred job"}
              {referredDeal?.stage === "delinquent" && " (delinquent)"}. The fee is earned at build
              completion, not collection — approve anyway if you're comfortable.
            </p>
          )}
          <div className="mt-2 flex gap-2">
            {hasBalance && !confirmOverBal ? (
              <button disabled={busy} onClick={() => setConfirmOverBal(true)}
                className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold disabled:opacity-50">
                ⚠ Approve anyway
              </button>
            ) : hasBalance && confirmOverBal ? (
              <>
                <button disabled={busy} onClick={() => run(async () => {
                  await approvePayout(r.id,
                    `Approved with ${money(outstanding)} outstanding on the referred job`, outstanding);
                  setConfirmOverBal(false);
                })} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold disabled:opacity-50">
                  Confirm — approve over {money(outstanding)}
                </button>
                <button onClick={() => setConfirmOverBal(false)} className="px-3 py-1.5 text-xs text-slate-500">
                  Cancel
                </button>
              </>
            ) : (
              <button disabled={busy} onClick={() => run(() => approvePayout(r.id))}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50">
                ✓ Approve payout
              </button>
            )}
            <button disabled={busy} onClick={() => run(async () => {
              const reason = window.prompt("Why is this fee not being paid?");
              if (reason) await voidPayout(r.id, reason);
            })} className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-600 disabled:opacity-50">
              Don’t pay
            </button>
          </div>
        </div>
      )}

      {canSeeMoney && r.payout_status === "approved" && (
        <div className="mt-3 rounded-lg bg-sky-50 border border-sky-200 p-3">
          <p className="text-xs font-semibold text-sky-900">
            Approved{r.payout_approved_at ? ` ${new Date(r.payout_approved_at).toLocaleDateString()}` : ""} —
            pay {money(r.payout_amount ?? REFERRAL_FEE)} to {r.referrer_name}
            {referrerAddress(r) && <span className="font-normal"> · {referrerAddress(r)}</span>}
          </p>
          {!paying ? (
            <button onClick={() => setPaying(true)}
              className="mt-2 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold">
              💵 Mark paid
            </button>
          ) : (
            <div className="mt-2 space-y-2">
              <div className="flex gap-2 flex-wrap">
                <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}
                  className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs">
                  <option value="check">Check</option>
                  <option value="ach">ACH</option>
                  <option value="cash">Cash</option>
                  <option value="gift_card">Gift card</option>
                  <option value="other">Other</option>
                </select>
                <input value={payRef} onChange={(e) => setPayRef(e.target.value)}
                  placeholder="Check # / reference"
                  className="flex-1 min-w-[140px] border border-slate-300 rounded-lg px-2 py-1.5 text-xs" />
              </div>
              <div className="flex gap-2">
                <button disabled={busy} onClick={() => run(async () => {
                  await markPayoutPaid(r.id, payMethod, payRef); setPaying(false);
                })} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50">
                  Confirm paid
                </button>
                <button onClick={() => setPaying(false)} className="px-3 py-1.5 text-xs text-slate-500">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {r.payout_status === "paid" && (
        <p className="mt-3 text-xs text-emerald-700 font-semibold">
          ✓ {money(r.payout_amount ?? REFERRAL_FEE)} paid{r.payout_date ? ` ${r.payout_date}` : ""}
          {r.payout_method ? ` · ${r.payout_method}` : ""}{r.payout_reference ? ` #${r.payout_reference}` : ""}
        </p>
      )}

      {/* Appointment set -> create the lead pre-filled, then link it. */}
      {apptOpen && (
        <ManualEntryModal
          rosterMembers={roster}
          actorId={actorId}
          defaultOwnerId={r.assigned_to ?? defaultOwnerId}
          prefill={{
            homeowner_name: r.referred_name,
            homeowner_phone: r.referred_phone ?? "",
            property_street: r.referred_street ?? "",
            property_city: r.referred_city ?? "",
            property_state: r.referred_state ?? "GA",
            property_zip: r.referred_zip ?? "",
            appointment_notes:
              `Referred by ${r.referrer_name}${r.notes ? ` — ${r.notes}` : ""}`,
          }}
          onClose={() => setApptOpen(false)}
          onCreated={async (dealId) => {
            setApptOpen(false);
            if (dealId) {
              // Linking is what keeps the referrer attached all the way to the
              // build — without it the $250 can never be traced back.
              await run(() => linkReferralToDeal(r.id, dealId));
            } else {
              onChanged();
            }
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function LogReferralModal({
  onClose, onLogged,
}: { onClose: () => void; onLogged: () => void }) {
  const [search, setSearch] = useState("");
  const [hits, setHits] = useState<DealLite[]>([]);
  const [picked, setPicked] = useState<DealLite | null>(null);
  const [f, setF] = useState({ name: "", phone: "", street: "", city: "", state: "GA", zip: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const doSearch = async () => {
    const s = search.trim();
    if (s.length < 2) return;
    setErr(null);
    const { data, error } = await supabase
      .from("deals")
      .select("id, homeowner_name, stage, build_completed_date, owner_id, split_rm_id, post_build_balance_remaining")
      .ilike("homeowner_name", `%${s}%`)
      .limit(10);
    if (error) { setErr(error.message); return; }
    setHits((data ?? []) as DealLite[]);
  };

  const submit = async () => {
    if (!picked || f.name.trim().length < 2) return;
    setBusy(true); setErr(null);
    try {
      const res = await logStaffReferral({
        referrerDealId: picked.id, name: f.name, phone: f.phone,
        street: f.street, city: f.city, state: f.state, zip: f.zip, notes: f.notes,
      });
      if (res?.duplicate) {
        window.alert("Logged. Heads up: this address is already in the CRM — it's flagged for Chad, and the fee still pays if it builds.");
      }
      onLogged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save the referral.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-bardin-ink">Log a referral</h2>
          <button onClick={onClose} className="text-slate-400 text-xl leading-none">×</button>
        </div>

        {!picked ? (
          <>
            <p className="text-sm text-slate-500 mb-2">Which customer is referring them?</p>
            <div className="flex gap-2">
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void doSearch(); }}
                placeholder="Search the referring customer by name…"
                className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <button onClick={() => void doSearch()}
                className="px-3 py-2 rounded-lg bg-slate-700 text-white text-sm font-semibold">Search</button>
            </div>
            <div className="mt-3 space-y-1">
              {hits.map((d) => (
                <button key={d.id} onClick={() => setPicked(d)}
                  className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">
                  <span className="font-semibold">{d.homeowner_name ?? "(no name)"}</span>
                  {d.stage && <span className="text-slate-400 text-xs"> · {d.stage.replace(/_/g, " ")}</span>}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm mb-3 flex justify-between items-center">
              <span>Referred by <span className="font-semibold">{picked.homeowner_name}</span></span>
              <button onClick={() => setPicked(null)} className="text-xs text-bardin-teal underline">change</button>
            </div>
            <div className="space-y-2">
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                placeholder="Their name *" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
                placeholder="Their cell" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <input value={f.street} onChange={(e) => setF({ ...f, street: e.target.value })}
                placeholder="Street address" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })}
                  placeholder="City" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                <input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })}
                  placeholder="ST" className="w-16 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                <input value={f.zip} onChange={(e) => setF({ ...f, zip: e.target.value })}
                  placeholder="ZIP" className="w-24 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
                rows={2} placeholder="Anything we should know?"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>
            {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
            <button disabled={busy || f.name.trim().length < 2} onClick={() => void submit()}
              className="mt-3 w-full py-2.5 rounded-lg bg-bardin-orange text-white font-semibold text-sm disabled:opacity-50">
              {busy ? "Saving…" : "Log referral"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
